import React, { useEffect } from "react";
import { v4 as uuidv4 } from "uuid";

import {
  Box,
  Button,
  Heading,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  useDisclosure,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
} from "@chakra-ui/react";
import { Plus, MoreVertical, Trash2, Archive, RefreshCw } from "lucide-react";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUserState } from "@state/userSlice/userSlice";

import { InviteUserModal } from "@components/InviteUserModal";
import { OurRoles, roles, userStatus, UserStatusNamesType } from "@hz/stealthy-gmc-cloaker-common/constants";
import {
  useDeleteSubUserMutation,
  useGetSubUsersQuery,
  useInviteSubUserMutation,
  useResendSubUserInviteMutation,
} from "@state/apiSlice/requests";
interface User {
  id: string;
  email: string;
  createdAt: string;
  status: UserStatusNamesType;
  role: OurRoles;
}
export const Users = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const navigate = useNavigate();
  const toast = useToast();
  const [sendInvite] = useInviteSubUserMutation();
  const [deleteUser] = useDeleteSubUserMutation();
  const [resendInvite] = useResendSubUserInviteMutation();

  const { userId, userRole } = useSelector(selectUserState);

  useEffect(() => {
    if (userRole === roles.STANDARD) {
      navigate("/");
    }
  }, [navigate]);

  const { data, isLoading } = useGetSubUsersQuery({});

  const formatDate = (dateString: Date) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  const getStatusColor = (status: UserStatusNamesType) => {
    switch (status) {
      case userStatus.USER_SETUP_COMPLETED:
        return "green";
      case userStatus.INVITED:
        return "yellow";
      case userStatus.DISABLED:
        return "red";
      default:
        return "gray";
    }
  };

  const handleInvite = async (values: { name: string; email: string; role: OurRoles }) => {
    try {
      const { name, email, role } = values;

      const result = await sendInvite({ name, email, role }).unwrap();

      if (result.success) {
        toast({
          title: `Invited ${name}`,
          status: "success",
          duration: 3000,
          position: "bottom-right",
        });
      }
    } catch (err) {
      toast({
        title: `Failed to send invite`,
        description: "Please try again later",
        status: "error",
        duration: 5000,
        position: "bottom-right",
      });
    } finally {
      onClose();
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      const result = await deleteUser({ userId }).unwrap();

      if (result.success) {
        toast({
          title: `Deleted User`,
          status: "success",
          duration: 3000,
          position: "bottom-right",
        });
      }
    } catch (err) {
      toast({
        title: `Failed to delete user`,
        description: "Please try again later",
        status: "error",
        duration: 5000,
        position: "bottom-right",
      });
    }
  };

  const handleReInvite = async (userId: string) => {
    try {
      const result = await resendInvite({ userId }).unwrap();

      if (result.success) {
        toast({
          title: `Invite resent`,
          status: "success",
          duration: 5000,
          position: "bottom-right",
        });
      }
    } catch (err) {
      toast({
        title: `Failed to resend invite`,
        description: "Please try again later",
        status: "error",
        duration: 5000,
        position: "bottom-right",
      });
    }
  };

  return (
    <DashboardLayout>
      <Box>
        <HStack justify="space-between" mb={8}>
          <Heading size="lg">Users</Heading>
          <Button
            leftIcon={<Plus size={20} />}
            onClick={onOpen}
            bg="#ff3d00"
            color="white"
            _hover={{
              bg: "#e53700",
              boxShadow: "0 0 20px rgba(255, 61, 0, 0.5)",
            }}
          >
            Invite User
          </Button>
        </HStack>

        {isLoading ? (
          <Text color="gray.400">Loading users...</Text>
        ) : data?.users.length === 0 ? (
          <Text color="gray.400">No users found.</Text>
        ) : (
          <TableContainer
            bg="gray.800"
            borderRadius="lg"
            border="1px"
            borderColor="gray.700"
            sx={{
              "& th": {
                borderBottom: "1px solid",
                borderColor: "gray.700",
                color: "gray.400",
                fontSize: "sm",
              },
              "& td": {
                borderBottom: "1px solid",
                borderColor: "gray.700",
              },
              "& tr:last-of-type td": {
                borderBottom: "none",
              },
            }}
          >
            <Table variant="unstyled">
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Email</Th>
                  <Th>Created At</Th>
                  <Th>Role</Th>
                  <Th>Status</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {data?.users.map((x) => {
                  const isCurrentUser = x.userId === userId;

                  return (
                    <Tr
                      key={uuidv4()}
                      _hover={{ bg: isCurrentUser ? "inherit" : "gray.700" }}
                      opacity={isCurrentUser ? 0.5 : 1}
                      pointerEvents={isCurrentUser ? "none" : "auto"}
                    >
                      <Td>
                        <Text color="white">{isCurrentUser ? `${x.name} (YOU)` : x.name}</Text>
                      </Td>
                      <Td>
                        <Text color="white">{x.email}</Text>
                      </Td>
                      <Td>
                        <Text color="gray.400">{formatDate(x.createdAt)}</Text>
                      </Td>
                      <Td>
                        <Badge bg="gray.700" color="white" px={2} py={1} borderRadius="md">
                          {x.role}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(x.status)}>
                          {x.status.charAt(0).toUpperCase() + x.status.slice(1)}
                        </Badge>
                      </Td>
                      <Td>
                        {!isCurrentUser && (
                          <Menu>
                            <MenuButton
                              as={Button}
                              variant="ghost"
                              size="sm"
                              color="gray.400"
                              _hover={{
                                bg: "gray.700",
                              }}
                            >
                              <MoreVertical size={16} />
                            </MenuButton>
                            <MenuList bg="black" borderColor="gray.700">
                              <MenuItem
                                icon={<Trash2 size={16} />}
                                bg="black"
                                _hover={{ bg: "gray.700" }}
                                onClick={() => handleDeleteUser(x.userId)}
                              >
                                Delete User
                              </MenuItem>

                              {x.status === userStatus.INVITED && (
                                <MenuItem
                                  icon={<RefreshCw size={16} />}
                                  bg="black"
                                  _hover={{ bg: "gray.700" }}
                                  onClick={() => handleReInvite(x.userId)}
                                >
                                  Re-Invite
                                </MenuItem>
                              )}
                            </MenuList>
                          </Menu>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>
      <InviteUserModal isOpen={isOpen} onClose={onClose} onSubmit={handleInvite} />
    </DashboardLayout>
  );
};
